/* ------------------------------------------------------------------
   zetse — the thinking behind the search box, kept apart from the
   sheet so the results page can use it too.

     correct(q)              -> { q: fixed, changed: bool }
     classesFor(q, products) -> [6, 7, 9, 10, 12] or []
     classNote(q, products)  -> { asked, available, base } or null
     askedClass(q)           -> 11, or 0
     classesInText(title)    -> [11]

   Two rules hold it together.

   Spelling is only fixed for a word the shop has never used, and the
   fixed wording has to find more than what was typed did.

   Classes come from two places at once: the generated index, and the
   classes named in the titles the search just returned. Either alone
   has a hole — the index goes stale between product loads, and ten
   predictive results cannot show every class a series covers — so a
   class either of them knows about counts as stocked. That matters:
   it is what stops the shop claiming it has no Class 11 of something
   when it does.
------------------------------------------------------------------- */
(function () {
  'use strict';

  var TERMS = null, RANK = null, SERIES = null;

  function build() {
    if (TERMS) return true;
    var idx = window.ZETSE_SEARCH_INDEX;
    if (!idx) return false;
    TERMS = String(idx.t || '').split(' ').filter(Boolean);
    RANK = Object.create(null);
    for (var i = 0; i < TERMS.length; i++) if (RANK[TERMS[i]] === undefined) RANK[TERMS[i]] = i;
    SERIES = Object.create(null);
    String(idx.s || '').split('|').forEach(function (row) {
      var c = row.lastIndexOf(':');
      if (c > 0) SERIES[row.slice(0, c)] = parseInt(row.slice(c + 1), 10) || 0;
    });
    return true;
  }

  /* Same shape the index was built with: lower case, punctuation out,
     and a run of single letters glued back together so “S.L. Arora”,
     “S L Arora” and “sl arora” all land on the same key. */
  function normalize(s) {
    s = String(s == null ? '' : s).toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim();
    if (!s) return '';
    var words = s.split(/\s+/), out = [], buf = '';
    for (var i = 0; i < words.length; i++) {
      var w = words[i];
      if (w.length === 1 && w >= 'a' && w <= 'z') { buf += w; }
      else { if (buf) { out.push(buf); buf = ''; } out.push(w); }
    }
    if (buf) out.push(buf);
    return out.join(' ');
  }

  /* ---------- spelling ---------- */

  /* Edit distance with transpositions counted as one slip, because on a
     phone keyboard they are. Rows are abandoned as soon as their best
     score is over budget. */
  function within(a, b, max) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    var prev2 = new Array(lb + 1), prev = new Array(lb + 1), cur = new Array(lb + 1), i, j;
    for (j = 0; j <= lb; j++) { prev[j] = j; prev2[j] = 0; }
    for (i = 1; i <= la; i++) {
      cur[0] = i;
      var rowBest = cur[0];
      for (j = 1; j <= lb; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 &&
            a.charAt(i - 1) === b.charAt(j - 2) &&
            a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur[j] = v;
        if (v < rowBest) rowBest = v;
      }
      if (rowBest > max) return max + 1;
      var t = prev2; prev2 = prev; prev = cur; cur = t;
    }
    return prev[lb];
  }

  /* Ties go to the commoner word — the index is ordered by how often it
     shows up in the catalogue, so the earlier match is the safer guess. */
  function nearest(word) {
    if (word.length < 4) return null;
    if (RANK[word] !== undefined) return null;
    var budget = word.length <= 6 ? 1 : 2;
    var best = null, bestDist = budget + 1, bestRank = Infinity;
    for (var i = 0; i < TERMS.length; i++) {
      var t = TERMS[i];
      if (t.length < 3) continue;
      var d = within(word, t, budget);
      if (d <= budget && (d < bestDist || (d === bestDist && i < bestRank))) {
        best = t; bestDist = d; bestRank = i;
      }
    }
    return best;
  }

  function correct(query) {
    var original = String(query == null ? '' : query);
    if (!build()) return { q: original, changed: false };
    var parts = original.split(/(\s+)/), changed = false;
    for (var i = 0; i < parts.length; i++) {
      var raw = parts[i];
      if (!raw || /^\s+$/.test(raw)) continue;
      var plain = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!plain || /^[0-9]+$/.test(plain)) continue;
      var fix = nearest(plain);
      if (fix && fix !== plain) { parts[i] = fix; changed = true; }
    }
    return { q: changed ? parts.join('').replace(/\s+/g, ' ').trim() : original, changed: changed };
  }

  /* ---------- classes ---------- */

  var ROMAN = { i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6, vii: 7, viii: 8, ix: 9, x: 10, xi: 11, xii: 12 };
  var NUMERAL = '\\d{1,2}|xii|xi|ix|viii|vii|vi|iv|iii|ii|x|v|i';
  var WORDS = 'class|classes|kaksha|std|grade|cls';

  function toClass(token) {
    token = String(token || '').toLowerCase();
    if (/^\d{1,2}$/.test(token)) {
      var n = parseInt(token, 10);
      return (n >= 1 && n <= 12) ? n : 0;
    }
    return ROMAN[token] || 0;
  }

  function flat(s) {
    return ' ' + String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() + ' ';
  }

  function sortNums(a) { return a.sort(function (x, y) { return x - y; }); }

  /* Every class a piece of text names — used on the titles the search
     returned, which is how this works for books the index never saw. */
  function classesInText(text) {
    var t = flat(text), out = [], seen = {};
    var re = new RegExp('\\b(?:' + WORDS + ')\\s+(' + NUMERAL + ')\\b', 'g');
    var m;
    while ((m = re.exec(t))) {
      var n = toClass(m[1]);
      if (n && !seen[n]) { seen[n] = 1; out.push(n); }
    }
    return sortNums(out);
  }

  function classesOfProducts(products) {
    var out = [], seen = {};
    (products || []).forEach(function (p) {
      classesInText((p && p.title) || '').forEach(function (n) {
        if (!seen[n]) { seen[n] = 1; out.push(n); }
      });
    });
    return sortNums(out);
  }

  /* The series without the class on it: “sl arora class 11” -> “sl arora”. */
  function stripClass(query) {
    return String(query == null ? '' : query)
      .replace(new RegExp('\\b(?:' + WORDS + ')\\s*[-–]?\\s*(?:' + NUMERAL + ')\\b', 'gi'), ' ')
      .replace(/\b\d{1,2}\s*(?:st|nd|rd|th)\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function seriesKey(query) { return normalize(stripClass(query)); }

  function unmask(mask) {
    var out = [];
    for (var c = 1; c <= 12; c++) if (mask & (1 << (c - 1))) out.push(c);
    return out;
  }

  function seriesClasses(query) {
    if (!build()) return [];
    var k = seriesKey(query);
    if (!k || k.length < 3) return [];
    return unmask(SERIES[k] || 0);
  }

  /* The class the customer asked for. “class 11”, “11th” and “XI” are
     plain enough; a bare trailing number is only read as a class when
     the rest of the query is a series the shop knows, so “part 2” and
     “vol 1” are left alone. */
  function askedClass(query) {
    var t = flat(query);
    var m = t.match(new RegExp('\\b(?:' + WORDS + ')\\s+(' + NUMERAL + ')\\b'));
    if (m) { var n = toClass(m[1]); if (n) return n; }
    m = t.match(/\b(\d{1,2})\s*(?:st|nd|rd|th)\b/);
    if (m) { var k = toClass(m[1]); if (k) return k; }
    m = t.match(/\s(\d{1,2})\s*$/);
    if (m && build()) {
      var rest = normalize(t.slice(0, m.index));
      if (rest.length >= 3 && SERIES[rest]) {
        var j = toClass(m[1]);
        if (j) return j;
      }
    }
    return 0;
  }

  function merge(a, b) {
    var seen = {}, out = [];
    a.concat(b).forEach(function (n) { if (n && !seen[n]) { seen[n] = 1; out.push(n); } });
    return sortNums(out);
  }

  /* Which classes to offer when no class was named. */
  function classesFor(query, products) {
    if (askedClass(query) || /neetu/i.test(query)) return [];
    var k = seriesKey(query);
    if (!k || k.length < 3) return [];
    var all = merge(seriesClasses(query), classesOfProducts(products));
    return all.length >= 2 ? all : [];
  }

  /* A class was asked for and nothing stocked answers it. Returns what
     the shop does have, so the page can say so rather than showing the
     wrong class without comment. */
  function classNote(query, products) {
    var asked = askedClass(query);
    if (!asked || /neetu/i.test(query)) return null;
    var all = merge(seriesClasses(query), classesOfProducts(products));
    if (!all.length) return null;
    if (all.indexOf(asked) !== -1) return null;
    return { asked: asked, available: all, base: stripClass(query) };
  }

  window.ZetseSmart = {
    normalize: normalize,
    correct: correct,
    stripClass: stripClass,
    askedClass: askedClass,
    classesInText: classesInText,
    classesOfProducts: classesOfProducts,
    seriesClasses: seriesClasses,
    classesFor: classesFor,
    classNote: classNote
  };
})();
